"use client";

import React, { useState } from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { AnimatePresence, motion } from "framer-motion";
import { Motion } from "@/components/global/utils/Motion";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";

import plusIcon from "@/public/icons/Add--large.svg";
import minusIcon from "@/public/icons/Subtract--large.svg";

import { PageCustomersSchema } from "@/sanity/pages/PageCustomersTemplate";

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

/**
 * The accordion behaviour is the one from `components/global/ui/FAQList` —
 * one answer open at a time, height animated open and closed — dressed in this
 * page's own card design (the ↳ marker, this page's type scale).
 */
export default function FAQSection({ data, className }: { data: PageCustomersSchema; className?: string }) {
  const items = data?.faqItems ?? [];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      className={cn(
        "relative bg-[#F8F7F6] px-[2rem] pt-[8rem] pb-[10rem] sm:px-[3rem] md:px-[5rem] lg:pt-[16rem] lg:pb-[12.5rem] border-t",
        className,
      )}>
      {/* the side rules run the length of the page — the FAQ frame in Figma
          omits them only because the Features frame's own pair extends down
          through this region behind it */}
      <DecorationLine
        className="top-0 left-[1rem] lg:left-[8.85%]"
        orientation="vertical"
        style="dark"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[8.85%]"
        orientation="vertical"
        style="dark"
      />

      <div className="relative z-[1] mx-auto flex w-[min(150.125rem,100%)] flex-col gap-[6rem] lg:flex-row lg:justify-between lg:gap-0">
        {/* left column — 350×… in the design, the heading only */}
        <div className="flex h-fit flex-col items-start lg:sticky lg:top-[10rem] lg:w-[43.75rem]">
          {data?.faqChip?.text && (
            <motion.div {...fadeInUp()}>
              <Chip
                className="mb-[2.5rem] [&_img]:!w-[2rem] [&_p]:font-geistmono [&_p]:text-[#202223] [&_p]:uppercase"
                image={data?.faqChip?.icon}
                text={data?.faqChip?.text}
              />
            </motion.div>
          )}

          {data?.faqTitle && (
            <motion.div
              {...fadeInUp(0.1)}
              className="pixel-highlight richtext richtext-start prose-em:!px-0 prose-em:font-ivy lg:prose-em:!px-[.7rem] prose-headings:mb-0 prose-headings:text-[4.5rem] prose-headings:leading-[1.1] lg:prose-headings:text-[6.5rem]">
              <PortableText
                value={data.faqTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        {/* right column — 589 wide, one card per question, one open at a time */}
        {items.length > 0 && (
          <div className="flex flex-col gap-[1rem] lg:w-[73.625rem]">
            {items.map((item, index) => {
              const open = openIndex === index;
              const answerId = `faq-answer-${index}`;

              return (
                <motion.div
                  key={index}
                  {...fadeInUp(index * 0.05)}
                  className="flex flex-col bg-[#EDECEB] p-[3rem] font-geist font-normal text-black transition-colors duration-300 hover:bg-[#E7E6E5]">
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={answerId}
                    onClick={() =>
                      setOpenIndex((current) => (current === index ? null : index))
                    }
                    className="flex w-full cursor-pointer items-center gap-[2rem] text-left leading-[1.3] uppercase">
                    <span
                      className="shrink-0 text-[2.5rem] whitespace-nowrap"
                      aria-hidden>
                      ↳
                    </span>
                    <p className="m-0 min-w-0 flex-1 text-[2.25rem] leading-[1.3]">{item?.question}</p>

                    {/* plus crossfades into minus — both glyphs come from the Figma assets */}
                    <span
                      className="relative block h-[3rem] w-[3rem] shrink-0"
                      aria-hidden>
                      <Image
                        className={cn(
                          "absolute inset-0 !h-full !w-full transition-all duration-300",
                          open && "rotate-90 opacity-0",
                        )}
                        src={plusIcon}
                        alt=""
                      />
                      <Image
                        className={cn(
                          "absolute inset-0 !h-full !w-full transition-all duration-300",
                          !open && "-rotate-90 opacity-0",
                        )}
                        src={minusIcon}
                        alt=""
                      />
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {open && item?.answer && (
                      <Motion
                        key="answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden">
                        <div
                          id={answerId}
                          className="richtext richtext-start w-full pt-[1rem] opacity-60 prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.4] prose-p:text-black">
                          <PortableText
                            value={item.answer}
                            components={richTextComponent}
                          />
                        </div>
                      </Motion>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
